import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, Users, MessageSquare, BookOpen, Megaphone, DollarSign, Globe, Settings,
  BarChart3, CreditCard, GraduationCap, FileText, Calendar, HeartPulse,
  Vote, Image, Bell, LogOut, Search, ExternalLink, UserCircle,
  Receipt, Mail, Headphones, MapPin, Landmark, Palette, ShieldCheck,
  Sparkles, ChevronRight, Menu, X, Bot, Eye, Building2, Wrench,
  UserCheck, ChevronDown, Layers, History, Linkedin, Send
} from 'lucide-react';
import { API } from '@/App';
import { clearFileSession } from '@/lib/fileSession';

// ─── 8 SECTIONS + TABS (from brief) ───────────────────────────────────────
const SECTIONS = [
  {
    id: 'accueil',
    label: 'Accueil',
    icon: Home,
    color: '#1e3a5f',
    tabs: [
      { id: 'dashboard', label: "Aujourd'hui", path: '/admin', icon: Home },
      { id: 'weekly', label: 'Résumé de la semaine', path: '/admin/resume-hebdo', icon: Sparkles },
      { id: 'stats', label: 'Statistiques', path: '/admin/analytics', icon: BarChart3 },
    ],
    roles: ['super_admin', 'regional', 'tresorier', 'auteur'],
  },
  {
    id: 'membres',
    label: 'Membres',
    icon: Users,
    color: '#7c3aed',
    tabs: [
      { id: 'members', label: 'Membres & adhésions', path: '/admin/members', icon: Users, badge: 'pending_adhesions' },
      { id: 'student-cards', label: 'Cartes étudiantes', path: '/admin/student-cards', icon: UserCheck, badge: 'pending_cards' },
      { id: 'directory', label: 'Fiches annuaire', path: '/admin/directory', icon: UserCircle },
      { id: 'regions', label: 'Régions & responsables', path: '/admin/bureaux-regionaux', icon: MapPin },
    ],
    roles: ['super_admin', 'regional', 'tresorier'],
  },
  {
    id: 'echanges',
    label: 'Échanges',
    icon: MessageSquare,
    color: '#0891b2',
    tabs: [
      { id: 'messaging', label: 'Messagerie', path: '/admin/messaging', icon: MessageSquare, badge: 'pending_forum' },
      { id: 'support', label: 'Support technique', path: '/admin/support', icon: Headphones, badge: 'open_tickets' },
    ],
    roles: ['super_admin', 'maintenance'],
  },
  {
    id: 'contenus',
    label: 'Contenus',
    icon: BookOpen,
    color: '#059669',
    tabs: [
      { id: 'copilot', label: 'Assistant IA', path: '/admin/copilot', icon: Sparkles },
      { id: 'formations', label: 'Formations', path: '/admin/formations', icon: GraduationCap },
      { id: 'articles', label: 'Articles', path: '/admin/articles', icon: FileText, badge: 'pending_articles' },
      { id: 'blog', label: 'Actualités', path: '/admin/blog', icon: Megaphone },
      { id: 'events', label: 'Événements', path: '/admin/events', icon: Calendar },
      { id: 'patients', label: 'Espace patients', path: '/admin/patient-content', icon: HeartPulse },
      { id: 'linkedin', label: 'LinkedIn', path: '/admin/actualite', icon: Linkedin },
    ],
    roles: ['super_admin', 'auteur'],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: Send,
    color: '#db2777',
    tabs: [
      { id: 'votes', label: 'Votes & sondages', path: '/admin/elections', icon: Vote },
      { id: 'banners', label: 'Bannières', path: '/admin/ad-banners', icon: Image },
      { id: 'notifications', label: 'Notifications', path: '/admin/notifications', icon: Bell },
    ],
    roles: ['super_admin'],
  },
  {
    id: 'finances',
    label: 'Finances',
    icon: DollarSign,
    color: '#d97706',
    tabs: [
      { id: 'contributions', label: 'Cotisations', path: '/admin/paiements', icon: CreditCard },
      { id: 'reminders', label: 'Relances', path: '/admin/renewal-reminders', icon: Mail },
      { id: 'receipts', label: 'Reçus fiscaux', path: '/admin/recus-fiscaux', icon: Receipt },
    ],
    roles: ['super_admin', 'regional', 'tresorier'],
  },
  {
    id: 'site',
    label: 'Site public',
    icon: Globe,
    color: '#35AFE4',
    tabs: [
      { id: 'overview', label: 'Toutes les pages', path: '/admin/site-overview', icon: Eye, match: ['/admin/site-pages/'] },
      { id: 'homepage', label: 'Accueil du site', path: '/admin/homepage-cms', icon: Home },
      { id: 'institutional', label: 'Institutionnel', path: '/admin/institutional-cms', icon: Building2 },
      { id: 'bureau', label: 'Bureau national', path: '/admin/bureau', icon: Landmark },
      { id: 'regional-pages', label: 'Pages régionales', path: '/admin/regional', icon: MapPin },
      { id: 'theme', label: 'Thème', path: '/admin/theme', icon: Palette },
    ],
    roles: ['super_admin', 'regional'],
  },
  {
    id: 'reglages',
    label: 'Réglages',
    icon: Settings,
    color: '#64748b',
    tabs: [
      { id: 'email-settings', label: 'Emails', path: '/admin/email-settings', icon: Mail },
      { id: 'security', label: 'Sécurité', path: '/admin/securite', icon: ShieldCheck },
      { id: 'roles', label: 'Rôles', path: '/admin/roles-permissions', icon: ShieldCheck },
      { id: 'settings', label: 'Paramètres', path: '/admin/settings', icon: Settings },
      { id: 'audit', label: 'Journal d\'audit', path: '/admin/audit-log', icon: History },
      { id: 'versions', label: 'Versions', path: '/admin/release-notes', icon: Layers },
    ],
    roles: ['super_admin'],
  },
];

const ROLE_LABELS = {
  super_admin: 'Administrateur',
  regional: 'Responsable régional',
  tresorier: 'Trésorier',
  auteur: 'Auteur',
  maintenance: 'Support technique',
  ophthalmologist: 'Ophtalmologiste',
  intern: 'Interne',
};

const tabMatches = (tab, pathname) =>
  pathname === tab.path
  || (tab.path !== '/admin' && pathname.startsWith(tab.path + '/'))
  || (tab.match || []).some(m => pathname.startsWith(m));

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user?.role || '';

  // Find active section based on current path
  const findActiveSection = useCallback(() => {
    for (const section of SECTIONS) {
      for (const tab of section.tabs) {
        if (tabMatches(tab, location.pathname)) {
          return section;
        }
      }
    }
    // Default to first section
    return SECTIONS[0];
  }, [location.pathname]);

  const activeSection = findActiveSection();

  // Remember last visited tab per section
  const [lastTabs, setLastTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_last_tabs') || '{}'); } catch { return {}; }
  });

  useEffect(() => {
    if (activeSection) {
      const tab = activeSection.tabs.find(t => tabMatches(t, location.pathname));
      if (tab) {
        setLastTabs(prev => {
          const next = { ...prev, [activeSection.id]: tab.id };
          localStorage.setItem('admin_last_tabs', JSON.stringify(next));
          return next;
        });
      }
    }
  }, [location.pathname, activeSection]);

  // Fetch pending counts
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && ['super_admin', 'maintenance'].includes(role)) {
      axios.get(`${API}/admin/pending-counts`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPendingCounts(r.data))
        .catch(() => {});
    }
  }, [location.pathname, role]);

  // Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(v => !v);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = () => {
    clearFileSession();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const visibleSections = SECTIONS.filter(s => s.roles.some(r => r === role));

  const handleSectionClick = (section) => {
    setMobileOpen(false);
    const lastTabId = lastTabs[section.id];
    const tab = lastTabId ? section.tabs.find(t => t.id === lastTabId) : section.tabs[0];
    navigate(tab?.path || section.tabs[0].path);
  };

  const getBadgeCount = (badgeKey) => {
    if (!badgeKey) return 0;
    return pendingCounts[badgeKey] || 0;
  };

  const getSectionBadge = (section) => {
    return section.tabs.reduce((sum, tab) => sum + getBadgeCount(tab.badge), 0);
  };

  // ─── SIDEBAR ──────────────────────────────────────────────────────────────
  const Sidebar = ({ isMobile = false }) => (
    <div className={`flex flex-col h-full bg-white ${isMobile ? 'w-72' : 'w-[72px]'}`} data-testid="admin-sidebar">
      {/* Logo */}
      <div className="py-4 px-2 flex items-center justify-center border-b border-gray-100">
        <Link to="/" data-testid="sidebar-logo">
          {isMobile ? (
            <img src="/SFOALC-logo-full.png" alt="SFOALC" className="h-10 w-auto" />
          ) : (
            <img src="/avatar-SFOALC.png" alt="SFOALC" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#35AFE4]/20 shadow-sm" />
          )}
        </Link>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ml-auto p-2 rounded-lg hover:bg-gray-100" data-testid="close-mobile-menu">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto py-2 px-1.5 scrollbar-thin">
        {visibleSections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection?.id === section.id;
          const badge = getSectionBadge(section);

          if (isMobile) {
            return (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section)}
                data-testid={`nav-section-${section.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-xl transition-all duration-200 ${
                  isActive ? 'text-white shadow-lg' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: section.color, boxShadow: `0 4px 12px ${section.color}30` } : {}}
              >
                <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
                <span className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>{section.label}</span>
                {badge > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : section.color }}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          }

          return (
            <div key={section.id} className="mb-1 relative group" data-testid={`nav-section-${section.id}`}>
              <button
                onClick={() => handleSectionClick(section)}
                className={`w-full flex flex-col items-center gap-0.5 py-2 px-1 rounded-xl transition-all duration-200 relative ${
                  isActive ? 'text-white' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: section.color } : {}}
                title={section.label}
              >
                <div className="relative">
                  <Icon size={20} strokeWidth={isActive ? 2.2 : 1.6} />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-[2px] flex items-center justify-center rounded-full text-[8px] font-bold text-white shadow-sm"
                      style={{ backgroundColor: isActive ? '#fff' : section.color, color: isActive ? section.color : '#fff' }}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] leading-tight text-center ${isActive ? 'font-bold' : 'font-semibold text-gray-500'}`}>
                  {section.label}
                </span>
              </button>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="border-t border-gray-100 p-2">
        {isMobile ? (
          <div className="flex items-center gap-3 p-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold">
              {(user?.first_name?.[0] || 'A')}{(user?.last_name?.[0] || '')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-900 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-[10px] text-gray-500">{ROLE_LABELS[role] || role}</p>
            </div>
            <button onClick={handleLogout} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50" data-testid="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <Link to="/dashboard" title="Mon compte" data-testid="user-avatar-link"
              className="w-10 h-10 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-[11px] font-bold shadow-md ring-2 ring-white hover:ring-[#35AFE4]/40 transition-all overflow-hidden">
              {user?.profile_image ? (
                <img src={user.profile_image.startsWith('http') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
              ) : null}
              <span style={{ display: user?.profile_image ? 'none' : 'block' }}>{(user?.first_name?.[0] || 'A')}{(user?.last_name?.[0] || '')}</span>
            </Link>
            <button onClick={handleLogout} className="w-9 h-8 flex justify-center items-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Déconnexion" data-testid="logout-btn">
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ─── TAB BAR ──────────────────────────────────────────────────────────────
  const TabBar = () => {
    if (!activeSection) return null;
    const tabs = activeSection.tabs.filter(t => {
      if (role === 'regional' && ['copilot', 'linkedin', 'blog'].includes(t.id)) return false;
      if (role === 'auteur' && ['copilot'].includes(t.id)) return false;
      return true;
    });

    return (
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin px-1" data-testid="admin-tab-bar">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tabMatches(tab, location.pathname);
          const badge = getBadgeCount(tab.badge);

          return (
            <Link
              key={tab.id}
              to={tab.path}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap transition-all duration-200 text-[13px] ${
                isActive
                  ? 'font-semibold text-white shadow-sm'
                  : 'font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
              style={isActive ? { backgroundColor: activeSection.color } : {}}
            >
              <Icon size={14} strokeWidth={isActive ? 2.2 : 1.6} />
              <span>{tab.label}</span>
              {badge > 0 && (
                <span className={`min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[9px] font-bold ${
                  isActive ? 'bg-white/30 text-white' : 'text-white'
                }`}
                  style={!isActive ? { backgroundColor: '#f59e0b' } : {}}>
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    );
  };

  // ─── SEARCH / COMMAND MODAL ───────────────────────────────────────────────
  const SearchModal = () => {
    if (!searchOpen) return null;

    const allTabs = SECTIONS.flatMap(s =>
      s.tabs.map(t => ({ ...t, section: s.label, sectionColor: s.color }))
    );
    const filtered = searchQuery
      ? allTabs.filter(t => t.label.toLowerCase().includes(searchQuery.toLowerCase()) || t.section.toLowerCase().includes(searchQuery.toLowerCase()))
      : allTabs;

    return (
      <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setSearchOpen(false)} data-testid="search-modal">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <Search size={18} className="text-gray-400" />
            <input
              autoFocus
              type="text"
              placeholder="Rechercher ou demander..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              data-testid="search-input"
            />
            <kbd className="hidden sm:inline text-[10px] text-gray-400 px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50">Échap</kbd>
          </div>
          <div className="max-h-[50vh] overflow-y-auto p-2">
            {filtered.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.path}
                  onClick={() => { navigate(tab.path); setSearchOpen(false); setSearchQuery(''); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tab.sectionColor}15` }}>
                    <Icon size={16} style={{ color: tab.sectionColor }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{tab.label}</p>
                    <p className="text-[11px] text-gray-400">{tab.section}</p>
                  </div>
                  <ChevronRight size={14} className="ml-auto text-gray-300" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-[#f8f9fb]" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex bg-white border-r border-gray-100 flex-shrink-0">
        <Sidebar />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 h-full bg-white shadow-xl">
            <Sidebar isMobile />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto min-w-0 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 flex-shrink-0" data-testid="admin-header">
          <div className="flex items-center gap-3 px-3 lg:px-5 py-2.5">
            {/* Mobile menu button */}
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-gray-100" data-testid="mobile-menu-btn">
              <Menu size={20} />
            </button>

            {/* Section name */}
            <div className="flex items-center gap-2 min-w-0">
              {activeSection && (
                <>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${activeSection.color}15` }}>
                    <activeSection.icon size={16} style={{ color: activeSection.color }} />
                  </div>
                  <h1 className="text-base font-bold text-gray-900 truncate hidden sm:block" data-testid="section-title">{activeSection.label}</h1>
                </>
              )}
            </div>

            {/* Search bar */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex-1 max-w-md mx-auto flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200/60 transition-colors cursor-pointer"
              data-testid="search-trigger"
            >
              <Search size={14} className="text-gray-400" />
              <span className="text-sm text-gray-400 flex-1 text-left">Rechercher ou demander...</span>
              <kbd className="hidden sm:inline text-[10px] text-gray-400 px-1.5 py-0.5 rounded border border-gray-200 bg-white">
                {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+K
              </kbd>
            </button>

            {/* Right actions */}
            <div className="flex items-center gap-1.5">
              <a href="/" target="_blank" rel="noopener noreferrer"
                className="hidden lg:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-gray-400 hover:text-[#35AFE4] hover:bg-[#eaf6fc] transition-all"
                data-testid="view-site-btn">
                <Globe size={13} /> Site <ExternalLink size={9} className="opacity-50" />
              </a>

              {/* Notification bell */}
              <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="notification-bell">
                <Bell size={18} className="text-gray-500" />
                {(pendingCounts.pending_adhesions || 0) + (pendingCounts.pending_articles || 0) > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
              </button>

              {/* Avatar */}
              <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-gray-200">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
                  {user?.profile_image ? (
                    <img src={user.profile_image.startsWith('http') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                  ) : null}
                  <span style={{ display: user?.profile_image ? 'none' : 'block' }}>{(user?.first_name?.[0] || 'A')}{(user?.last_name?.[0] || '')}</span>
                </div>
                <div className="hidden xl:block">
                  <p className="text-[11px] font-bold text-gray-900 leading-tight">{user?.first_name} {user?.last_name}</p>
                  <p className="text-[9px] text-gray-500">{ROLE_LABELS[role] || role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab bar */}
          <div className="border-t border-gray-50 px-3 lg:px-5 py-1">
            <TabBar />
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 min-h-0" style={{ fontFamily: "'Nunito', 'Roboto', sans-serif" }}>
          {children}
        </div>
      </main>

      {/* Search modal */}
      <SearchModal />
    </div>
  );
};

export default AdminLayout;
